import classNames from 'classnames';
import type { ChangeEvent } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { DataAttributes } from 'shared/lib/data-auto-test';
import type { CampaignTypeKey } from 'entities/campaign';
import { isCampaignType } from 'entities/campaign';
import { MaterialIcon } from 'shared/ui/MaterialIcon';

import style from './CampaignTypeSelector.module.scss';

interface CampaignTypeSelectorProps {
  value: CampaignTypeKey;
  inputName: string;
  onSelect: (value: CampaignTypeKey) => void;
  isDisabled?: boolean;
  attributes?: DataAttributes['attributes'];
  isWide?: boolean;
}

const data: Record<CampaignTypeKey, { iconName: string; title: string; text: string }> = {
  lead: {
    iconName: 'group',
    title: 'Leads',
    text: 'Get prospect contact details and your in-house sales team can follow-up.',
  },
  appt: {
    iconName: 'call',
    title: 'Call Center Leads',
    text: 'Our call center handles these calls providing exclusive, qualified leads. You pay per lead.',
  },
  sales: {
    iconName: 'paid',
    title: 'Sales',
    text: 'Get leads without any upfront costs and pay us only when a net sale takes place.',
  },
};

export function CampaignTypeSelector({
  value,
  inputName,
  onSelect,
  isDisabled = false,
  attributes,
  isWide = false,
}: CampaignTypeSelectorProps) {
  const { register } = useFormContext();
  const isActive = useWatch({ name: inputName }) === value;

  const { iconName, title, text } = data[value];
  const selectCampaignType = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (isCampaignType(value)) {
      onSelect(value);
    }
  };

  return (
    <div {...attributes}>
      <input
        type="radio"
        id={value}
        value={value}
        {...register(inputName, {
          onChange: selectCampaignType,
        })}
        style={{ display: 'none' }}
        disabled={isDisabled}
      />
      <label
        htmlFor={value}
        className={classNames(
          style.root,
          `grid items-start py-8 border rounded-xl border-deep-blue-100
          text-center cursor-pointer hover:outline hover:outline-2 hover:outline-deep-blue-500 hover:bg-deep-blue-25 min-h-full max-w-[264px]`,
          { [style.isActive]: isActive, [style.isDisabled]: isDisabled }
        )}
      >
        <div>
          <MaterialIcon
            className={classNames(style.icon, {
              'text-white': isActive,
              'text-deep-blue-200': isDisabled,
              'text-deep-blue-500': !isActive && !isDisabled,
            })}
          >
            {iconName}
          </MaterialIcon>
          <div
            className={classNames(style.title, {
              [style.isActive]: isActive,
              [style.isDisabled]: isDisabled,
            })}
          >
            {title}
          </div>
          <div
            className={classNames(
              style.text,
              { [style.isActive]: isActive, [style.isDisabled]: isDisabled, 'px-7': isWide, 'px-2': !isWide },
              'mt-2 font-normal text-sm text-center'
            )}
          >
            {text}
          </div>
        </div>
      </label>
    </div>
  );
}
